import { createApp } from 'vue'
import Notifications from '@kyvg/vue3-notification'
import moment from 'moment'
import currency from './helpers/currency'
import { resolveItemImageUrl } from './helpers/itemImageUrl'
import { ModelListSelect } from 'vue-search-select'
import VueFormWizard from 'vue-form-wizard'
import vSelect from "vue-select";
import swal from 'sweetalert2'

import App from './app'
import router from './router'
import { store } from './store/store'
import auth from './helpers/auth'
import { beginSessionExpiryHandling } from './helpers/sessionExpiry'
import { initScrollRestoration, resetAppScroll } from './helpers/scrollToTop'

import images from './components/common/image/images'
import overlay from './components/loader/overlay/overlay'
import loader from './components/loader/spinner/spinner'
import select from './components/common/select/select'
import TextInput from './components/common/TextInput'
import TextArea from './components/common/TextArea'
import FieldLabel from './components/common/FieldLabel'
import QuantityStepper from './components/common/QuantityStepper'
import StickyFormFooter from './components/common/StickyFormFooter'
import Pagination from './components/common/Pagination'
import FormError from './components/common/formerror/FormError'
import Checkbox from './components/common/Checkbox'
import AccordionToggle from './components/common/AccordionToggle'
import FieldError from './components/common/FieldError.vue'
import StandardModal from './components/common/StandardModal.vue'
import AttachmentCard from './components/common/AttachmentCard.vue'

import jQuery from 'jquery'
import semantic from 'semantic'
import axios from 'axios'
import dinero from 'dinero.js'

let vm = null

// In production (e.g. DigitalOcean Static Site), point API at backend URL
if (process.env.VUE_APP_BASE_URL) {
  axios.defaults.baseURL = process.env.VUE_APP_BASE_URL;
  // Only strip leading /api when baseURL already ends with /api (local and DO both use base URL ending with /api)
  axios.interceptors.request.use(config => {
    const base = axios.defaults.baseURL ? String(axios.defaults.baseURL).replace(/\/$/, '') : '';
    if (config.url && config.url.startsWith('/api/') && base.endsWith('/api')) {
      config.url = config.url.replace(/^\/api/, '');
    }
    return config;
  });
}
axios.defaults.headers.common = {
  'Accept': 'application/json'
}

// Add axios interceptor to automatically include auth token
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

const globalMixin = {
  methods: {
    getValidationErrors: function () {
      return this.$store.getters['getValidationErrors']
    },
    validation: function (input) {
      if (this.$store.getters['getValidationErrors']) {
        if (this.$store.getters['getValidationErrors'].hasOwnProperty(input)) {
          return true
        }
      }
      return false
    },
    clearValidationErrors: function () {
      this.$store.commit('setValidationErrors', null)
    },
    successNotification: function (message = 'Saved!') {
      this.$notify({
        group: 'app',
        position: 'top-right',
        type: 'success',
        text: message
      })
    },
    errorNotification: function (message = 'Whoops, something unexpected occured!') {
      this.$notify({
        group: 'app',
        position: 'top-right',
        type: 'error',
        text: message
      })
    },
    warningNotification: function (message) {
      this.$notify({
        group: 'app',
        position: 'top-right',
        type: 'warn',
        text: message
      })
    },
    showOverlay: function () {
      this.$store.commit('setOverlay', true)
    },
    closeOverlay: function () {
      setTimeout(function () {
        if (vm && vm.$store) vm.$store.commit('setOverlay', false)
      }, 800)
    },
    getImageUrl: function (item) {
      const placeholder = require('./assets/img/no-image-available.jpg')
      if (!item) return placeholder
      if (typeof item === 'string') {
        // Check if it's already a full URL (from Spaces CDN)
        if (item.startsWith('http')) return item
        return store.getters['getImageUrl'] + item
      }
      return resolveItemImageUrl(item, store.getters['getImageUrl'], placeholder)
    },
    scrollToTop: function () {
      resetAppScroll()
    },
    bodyScrollToggle: function () {
      document.body.classList.toggle('no-scroll')
    },
    currency: currency
  }
}

/*
 * Global components
 */
// Before the router's first navigation, sync session so guards and /api/auth/user see a logged-in user.
;(function primeSessionFromStorage () {
  if (typeof localStorage === 'undefined') return
  try {
    const t = localStorage.getItem('token')
    if (!t) return
    store.commit('user/setLoggedIn')
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + t
    axios.defaults.headers.common['Accept'] = 'application/json'
  } catch (e) {
    /* ignore */
  }
})()

// OAuth return uses /?xero=... so redirects are reliable (no #). Before the first route resolves,
// rewrite to /settings/account?... so the app boots on Integrations flow — not on / (quotes).
;(function normalizeXeroOAuthUrl () {
  if (typeof window === 'undefined') return
  try {
    const sp = new URLSearchParams(window.location.search || '')
    const xero = sp.get('xero')
    if (!xero) return
    const normPath = (window.location.pathname || '/').replace(/\/$/, '') || '/'
    if (normPath !== '/') return
    if (!localStorage.getItem('token')) return
    const nq = new URLSearchParams()
    nq.set('xero', xero)
    nq.set('tab', sp.get('tab') || 'integrations')
    const msg = sp.get('message')
    if (msg) nq.set('message', msg)
    window.history.replaceState(window.history.state, '', '/settings/account?' + nq.toString())
  } catch (e) {
    /* ignore */
  }
})()

initScrollRestoration()

const app = createApp(App)
app.use(Notifications)
app.use(VueFormWizard)
app.use(router)
app.use(store)

app.config.globalProperties.moment = moment
app.config.globalProperties.currency = currency
app.config.globalProperties.swal = swal
app.mixin(globalMixin)

app.component('app-images', images)
app.component('app-loader', loader)
app.component('app-overlay', overlay)
app.component('app-select', select)
app.component('app-text-input', TextInput)
app.component('app-text-area', TextArea)
app.component('app-field-label', FieldLabel)
app.component('app-quantity-stepper', QuantityStepper)
app.component('app-sticky-form-footer', StickyFormFooter)
app.component('app-pagination', Pagination)
app.component('app-search-select', ModelListSelect)
app.component('v-select', vSelect)
app.component('app-form-error', FormError)
app.component('app-checkbox', Checkbox)
app.component('app-accordion-toggle', AccordionToggle)
app.component('app-field-error', FieldError)
app.component('app-standard-modal', StandardModal)
app.component('app-attachment-card', AttachmentCard)

vm = app.mount('#app')

if (localStorage.getItem('token') != null) {
  vm.$store.commit('user/setLoggedIn')
  axios.defaults.headers.common = {
    'Authorization': 'Bearer ' + localStorage.getItem('token'),
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
} else {
  // auth.logout(this)
}

router.isReady().then(() => {
  // auth.check(this)
})

/*
 * Axios request interceptor, use this to peform actions before each request is sent
 */
axios.interceptors.request.use(function (request) {
  vm.clearValidationErrors()
  // The global 'Content-Type: application/json' default makes axios serialise FormData bodies to JSON.
  // Drop it so the browser sets multipart/form-data with the correct boundary.
  if (typeof FormData !== 'undefined' && request.data instanceof FormData && request.headers) {
    if (typeof request.headers.delete === 'function') {
      request.headers.delete('Content-Type')
    } else {
      delete request.headers['Content-Type']
      if (request.headers.common) delete request.headers.common['Content-Type']
      if (request.headers.post) delete request.headers.post['Content-Type']
    }
  }
  return request
}, null)

/*
 * Axios response interceptor, use this to perform actions to a response before its passed to the caller
 */
function isAuthFormRequest (config) {
  const url = String((config && config.url) || '')
  return /\/auth\/(login|register)(\?|$)/.test(url)
}

function handleSessionExpiry () {
  if (!beginSessionExpiryHandling()) return
  // Drop any stacked toasts from parallel 401/403s, then show one.
  if (vm && typeof vm.$notify === 'function') {
    vm.$notify({ group: 'app', clean: true })
  }
  vm.errorNotification('Authentication failed. Please login again.')
  auth.logout(vm)
}

axios.interceptors.response.use(null, function (exception) {
  if (exception.response) {
    if (exception.response.status === 500) {
      vm.errorNotification('Server error occurred')
    } else if (exception.response.status === 422) {
      store.commit('setValidationErrors', exception.response.data.errors)
    } else if (exception.response.status === 401 || exception.response.status === 403) {
      // Router guard probes /api/auth/user on public routes with a stale token; let the guard clear
      // session without forcing /login + toast (e.g. email verification link must show /verify).
      if (exception.config && exception.config._publicRouteProbe) {
        return Promise.reject(exception)
      }
      // Failed login/register (incl. Turnstile 403) should show the form's own error, not force logout.
      if (store.getters['user/isLoginInProgress'] || isAuthFormRequest(exception.config)) {
        return Promise.reject(exception)
      }
      handleSessionExpiry()
    } else if (exception.response.status === 400) {
      // Handle validation errors without logging out
      vm.errorNotification('Invalid request: ' + (exception.response.data.error || 'Bad request'))
    } else {
      // Handle other errors without logging out
      vm.errorNotification('Request failed: ' + (exception.response.data.error || 'Unknown error'))
    }
  } else {
    vm.errorNotification('Network error occurred')
  }
  return Promise.reject(exception)
})
